"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { X, Sparkles, Megaphone } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { createGoogleAuthPopupStore } from "@/stores/auth-form-stores";
import { useScopedStore } from "@/stores/scoped-store";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function GoogleAuthPopup() {
  const pathname = usePathname();
  const { user, initialized } = useAuthStore();
  const { isOpen, mounted, setIsOpen, setMounted } = useScopedStore(
    "google-auth-popup",
    createGoogleAuthPopupStore,
    (state) => state
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !initialized) return;

    if (pathname.startsWith("/auth")) {
      setIsOpen(false);
      return;
    }

    if (user) {
      setIsOpen(false);
      return;
    }

    const isDismissed = sessionStorage.getItem("nv_google_auth_dismissed") === "true";
    if (isDismissed) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, [mounted, initialized, user, pathname]);

  const handleDismiss = () => {
    setIsOpen(false);
    sessionStorage.setItem("nv_google_auth_dismissed", "true");
  };

  if (!mounted || !isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleDismiss(); }}>
      <DialogContent
        overlayClassName="bg-black/60 backdrop-blur-sm"
        hideClose
        className="max-w-[440px] border-0 bg-transparent p-0 shadow-none sm:rounded-2xl"
      >
        <div className="auth-popup-shell auth-popup-card relative overflow-hidden rounded-2xl border border-white/15 bg-[#050505] px-6 pb-6 pt-8 text-white shadow-[0_34px_90px_rgba(0,0,0,0.72)] sm:px-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-white/14 via-white/[0.04] to-transparent"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#ff3338]/18 to-transparent"
          />
          <p
            aria-hidden
            className="pointer-events-none absolute -bottom-4 -right-2 select-none font-display text-[5rem] leading-none text-white/[0.06]"
          >
            join.
          </p>

          <div className="absolute right-4 top-4 z-10">
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-black/35 text-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="relative text-center">
            <p className="mx-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white">
              <Megaphone className="h-3 w-3" />
              For local community
            </p>

            <p className="mt-4 text-[10px] font-black uppercase tracking-[0.25em] text-emerald-300">
              Welcome to Sydney&apos;s Hub
            </p>

            <DialogTitle className="font-display mt-2 text-center text-2xl font-black tracking-tight text-white sm:text-3xl">
              Join{" "}
              <span className="font-script font-semibold normal-case tracking-normal">Neighbourhood Voice</span>
            </DialogTitle>

            <DialogDescription className="mt-3 text-center text-sm leading-relaxed text-white/80">
              Unlock local deals, track events, discover curated eats, and save your favourite suburban spaces.
            </DialogDescription>

            <div className="mt-6">
              <GoogleSignInButton variant="dark" label="Continue with Google" />
            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-center gap-2 text-xs text-white/60">
                <span>Or use standard access:</span>
                <Link
                  href="/auth/login"
                  onClick={handleDismiss}
                  className="font-semibold text-emerald-300 hover:underline"
                >
                  Sign in with email
                </Link>
              </div>

              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs font-semibold text-white/60 transition-colors hover:text-white"
              >
                Skip for now
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.07] px-3 py-2 text-[10px] text-white/70">
              <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
              <span>Fast setup. No password required.</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
